import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import Button from "@mui/material/Button";
import s from "../../page/SurveyAnswer/style.module.scss";
import Container from "../Container";
import AccountMenu from "../AccountMenu";
import Footer from "../Footer";
import ProgressBar from "../ProgressBar";
import RenderQuestion from "../RenderQuestion";
import { displayDateMessage } from "../../../utils/validateDate";
import { getTranslation } from "../../../locales";
import { validateSingleQuestion } from "../../../utils/validateQuestions";
import { setFocus } from "../../../utils/setFocus";
import {
  FORMATING_TYPE,
  MAX_MOBILE_SIZE,
  QUESTION_TYPES,
  RESUME,
} from "../../constants";
import { EQUATION } from "../../../services/constants";

const RenderSurveyInGuidedMode = ({
  hasConditions,
  hasAnswerOptionLogic,
  hasDependentEV,
  activeGroup,
  hiddenGroup = [],
  surveyId,
  currentLang,
  handleValidateQuestion,
  onAutoSave,
  resumeType,
  questionsCount = 0,
  determineTerminateConditionsValidity,
}) => {
  const [answers, setAnswers] = useState({});
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [currentQuestionValid, setCurrentQuestionValid] = useState(true);
  const [completedQuestions, setCompletedQuestions] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState([]);
  const [prev, setPrev] = useState(false);
  const answerContactRef = useRef(null);
  const { previewing, hasMobileAuth } = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      previewing: urlParams.get("previewing"),
      hasMobileAuth: urlParams.get("hasMobileAuth"),
    };
  }, []);

  const {
    currentSurveyId,
    currentTheme,
    surveyExperienceType,
    data: survey,
  } = useSelector((state) => state.survey);
  const { visibleThemeHeaderNLogo } =
    survey[currentSurveyId].thirdPartySettings;

  const getFilteredQuestions = (questions) => {
    if (previewing) {
      return questions.filter(
        (ques) =>
          ques.type !== QUESTION_TYPES.BLOCK &&
          ques.type !== "variable" &&
          ques.type !== EQUATION &&
          ques.type !== QUESTION_TYPES.TERMINATE
      );
    } else {
      return questions.filter(
        (ques) =>
          ques.type !== QUESTION_TYPES.BLOCK &&
          ques.type !== "variable" &&
          ques.type !== EQUATION &&
          ques.type !== "hidden" &&
          !ques.formattingType &&
          ques.formattingType !== FORMATING_TYPE.PASS_THROUGH_LINK
      );
    }
  };
  const { data: autoSavedData, dataExist: autoSaveExist } = useSelector(
    (state) => state.autoSave
  );
  const questions = useMemo(() => {
    let questions = activeGroup || survey[surveyId]?.questions;
    return getFilteredQuestions(questions);
  }, [activeGroup, survey, surveyId]);
  const anonymousAccess =
    Object.values(survey)[0]?.thirdPartySettings?.anonymous_access || false;
  const isMobile = window.innerWidth < MAX_MOBILE_SIZE;

  const viewHeight = useMemo(() => {
    const height = window.innerHeight - 60;
    if (currentSurveyId && survey?.[currentSurveyId]?.thirdPartySettings) {
      const { visibleThemeHeaderNLogo } =
        survey[currentSurveyId].thirdPartySettings;
      if (visibleThemeHeaderNLogo) {
        return height - 64; // SurveyHeader height
      } else {
        return height;
      }
    }
  }, [currentSurveyId, survey]);

  const showProgressBar = useMemo(() => {
    return !!(
      surveyId && survey?.[surveyId]?.thirdPartySettings?.visibleProgressBar
    );
  }, [survey, surveyId]);

  useEffect(() => {
    if (currentQuestionValid) {
      setFocus(questions[currentQuestionIdx]?.id + "container");
    } else {
      setFocus(questions[currentQuestionIdx]?.id + "container", true);
    }
  }, [currentQuestionValid, currentQuestionIdx, questions]);

  useEffect(() => {
    const totalQues = getFilteredQuestions(survey[surveyId]?.questions);
    setTotalQuestions(totalQues);
  }, [surveyId, survey]);

  useEffect(() => {
    if (prev) {
      setFocus(questions[currentQuestionIdx].id + "container");
    }
  }, [prev, currentQuestionIdx, questions]);

  useEffect(() => {
    if (resumeType === RESUME && autoSaveExist) {
      setAnswers(autoSavedData.answers);
      setCompletedQuestions(autoSavedData.completed_questions);
      setCurrentQuestionIdx(autoSavedData.current_question_idx);
    }
  }, [resumeType]);

  useEffect(() => {
    const dataToSave = {
      answers,
      current_question_idx: currentQuestionIdx,
      completed_questions: completedQuestions,
    };
    onAutoSave(dataToSave);
  }, [answers, currentQuestionIdx]);

  const handlePrevQuestionClick = () => {
    window.scrollTo(0, 0);
    if (currentQuestionIdx === 0) {
      return;
    }
    setCurrentQuestionIdx((currentQuestionIdx) => currentQuestionIdx - 1);
    setCompletedQuestions((prevCompletedQues) => prevCompletedQues - 1);
    setCurrentQuestionValid(true);
    setPrev(true);
  };

  const handleNextQuestionClick = () => {
    window.scrollTo(0, 0);
    const nextQuestionIndex = currentQuestionIdx + 1;
    const isValid = validateSingleQuestion(
      surveyExperienceType,
      questions[currentQuestionIdx],
      answers,
      [],
      answerContactRef,
      setCurrentQuestionValid
    );

    if (isValid) {
      setCurrentQuestionValid(true);
      setCompletedQuestions((prevCompletedQues) => prevCompletedQues + 1);
      if (questions?.length && nextQuestionIndex < questions.length) {
        setCurrentQuestionIdx(nextQuestionIndex);
        // Call for determining terminate Validity for conditional survey's,
        // after every answer submitted.
        determineTerminateConditionsValidity &&
          determineTerminateConditionsValidity(answers);
      } else if (nextQuestionIndex === questions?.length) {
        setCurrentQuestionIdx(0);
        handleValidateQuestion(answers);
      }
    }
  };

  if (questions?.length) {
    const nextOrSubmit =
      currentQuestionIdx + 1 === questions.length && hiddenGroup.length <= 0
        ? "submit"
        : "next";
    const q = questions[currentQuestionIdx];

    return (
      <>
        <div style={{ minHeight: viewHeight }}>
          <Container className={s.guideMain}>
            <div className={s.surveyContainer}>
              <div className={s.surveyQuestionsWrapper}>
                <div className={s.question} id={q.id + "container"}>
                  <RenderQuestion
                    questions={survey[surveyId]?.questions} //to fetch the options from answers for range categorical
                    q={q}
                    answers={answers}
                    setAnswers={setAnswers}
                    surveyId={surveyId}
                    ref={answerContactRef}
                    currentLang={currentLang}
                  />
                  {q.type === "datetime" ? (
                    <p className={s.errorText} id={`${q.id}-error`}>
                      {displayDateMessage(
                        q,
                        currentLang,
                        surveyExperienceType,
                        currentQuestionValid,
                        false,
                        answers
                      )}
                    </p>
                  ) : (
                    <p
                      className={s.errorText}
                      id={`${q.id}-error`}
                      aria-label={
                        !currentQuestionValid
                          ? getTranslation(
                              currentLang,
                              "surveyTaker:requiredFieldError"
                            )
                          : ""
                      }
                    >
                      {!currentQuestionValid
                        ? getTranslation(
                            currentLang,
                            "surveyTaker:requiredFieldError"
                          )
                        : ""}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Container>
          <div className={s.progressWrapper}>
            <div
              className={
                isMobile && visibleThemeHeaderNLogo
                  ? s.styleMobileObj
                  : s.styleDesktopObj
              }
            >
              {totalQuestions[0]?.id === q.id ||
              hasConditions ||
              hasDependentEV ||
              hasAnswerOptionLogic ? (
                <div className={s.questionIndex} />
              ) : (
                <Button
                  className={s.button}
                  style={{
                    color: "#333333",
                    outline: "none",
                  }}
                  data-testid="previous"
                  variant="text"
                  color="primary"
                  onClick={handlePrevQuestionClick}
                >
                  {getTranslation(currentLang, "surveyTaker:previous")}
                </Button>
              )}
              <div
                className={(hasConditions || hasAnswerOptionLogic) && s.next}
              >
                <Button
                  data-testid={nextOrSubmit}
                  className={
                    hasConditions || hasAnswerOptionLogic ? s.submit : s.button
                  }
                  style={{
                    backgroundColor: currentTheme.primaryColor,
                    color: currentTheme.fontColor,
                  }}
                  variant="contained"
                  color="primary"
                  onClick={handleNextQuestionClick}
                >
                  {getTranslation(currentLang, `surveyTaker:${nextOrSubmit}`)}
                </Button>
              </div>
            </div>
          </div>
          {showProgressBar ? (
            <ProgressBar
              totalQuestions={
                questionsCount ? questionsCount : totalQuestions.length
              }
              completedQuestions={completedQuestions}
              currentTheme={currentTheme}
              showPercentage={true}
            />
          ) : null}
        </div>
        <div>
          {isMobile &&
            (visibleThemeHeaderNLogo ? (
              ""
            ) : (
              <div className={s.topheader}>
                <div className={s.privacyPolicy}>
                  <AccountMenu
                    hasMobileAuth={hasMobileAuth && hasMobileAuth === "true"}
                    surveyTaker={true}
                    anonymousAccess={anonymousAccess}
                  />
                </div>
              </div>
            ))}
          <Footer isMobile={isMobile} currentLang={currentLang} />
          {isMobile && <div className={s.button}></div>}{" "}
          {/* Needed: adding a paading for privacy policy */}
        </div>
      </>
    );
  }
  return null;
};

export default RenderSurveyInGuidedMode;
